<template>
  <div class="app question">
    <b-loading :is-full-page="true" v-model="isLoading"></b-loading>

    <div class="p-4 header">
      <p class="has-text-white has-text-centered is-size-5">{{ question.title }}</p>
    </div>

    <div class="p-4">
      <div class="title-detail">
        <p>{{ question.detail }}</p>
      </div>

      <div v-for="topic in topics" :key="topic.id">
        <div class="p-4 my-3 topic">
          <p style="font-size: 20px">
            {{ topic.number }}. {{ topic.name }}
            <span v-if="isRequired(topic)" class="required-mark">*</span>
            <span v-else class="optional-mark">{{ isNarak ? "(optional)" : "(ไม่บังคับ)" }}</span>
          </p>
        </div>

        <div v-if="topic.type === 'DROPDOWN'">
          <b-autocomplete
            v-model="topic.search"
            :data="filterChoiceNames(topic)"
            :placeholder="isNarak ? 'Select or type to search' : 'เลือกหรือพิมพ์ค้นหา'"
            open-on-focus
            clearable
            keep-first
            max-height="240px"
            @input="value => clearAnswerWhenEdited(topic, value)"
            @select="option => (topic.answer = option)"
          >
            <template #empty>{{ isNarak ? "No results" : "ไม่พบตัวเลือก" }}</template>
          </b-autocomplete>
        </div>

        <div v-if="topic.type === 'TEXT'">
          <b-input v-model="topic.answer"></b-input>
        </div>

        <div class="choices" v-for="(choice, index) in topic.questionaireChoices" :key="index">
          <div v-if="topic.type === 'SINGLE'">
            <b-radio v-model="topic.answer" :name="choice.name" :native-value="choice.name">
              <p style="font-weight: normal">{{ choice.name }}</p>
            </b-radio>
          </div>

          <div v-if="topic.type === 'MULTI'">
            <b-checkbox v-model="topic.answer" :name="choice.name" :native-value="choice.name">
              <p style="font-weight: normal">{{ choice.name }}</p>
            </b-checkbox>
          </div>
        </div>
      </div>

      <div class="mt-5">
        <b-field>
          <b-checkbox v-model="pdpa" name="pdpa">
            <template v-if="isKn">
              <p>ยอมรับ
                <a href="https://knmedical.co.th/privacy-policy/" target="_blank">นโยบายความเป็นส่วนตัว</a>
              </p>
            </template>
            <template v-else-if="!isNarak">
              <p>ยอมรับ
                <a href="https://pdpa.pro/policies/view/th/QuGMzMzZbQxNyCruaSY4iYSw"
                  target="_blank">เงื่อนไขการให้บริการ
                  และ
                  นโยบายความเป็นส่วนตัว</a>
              </p>
            </template>
            <template v-else>
              <p>Accept
                <a href="https://pdpa.pro/policies/view/en/QuGMzMzZbQxNyCruaSY4iYSw" target="_blank">Terms and
                  Conditions</a>
              </p>
            </template>
          </b-checkbox>
        </b-field>
      </div>

      <b-button @click="sendQuestion()" type="is-primary" class="mt-6 is-rounded" size="is-medium" expanded
        :disabled="isSendQuestionDisabled" :loading="isSubmitting"><span v-if="!isNarak">ส่งคำตอบ</span><span v-else>Submit</span></b-button>
      <p v-if="submitErrorMessage" class="mt-3 has-text-centered submit-error">{{ submitErrorMessage }}</p>
    </div>
  </div>
</template>

<script>
import { COMPANY_IDS } from "../company-ids";

const ANSWER_SAVED_STATUS_CODE = 201;
const SUBMIT_ERROR_MESSAGES = {
  403: { th: "ไม่พบการสแกนของ code นี้ กรุณาสแกน QR code อีกครั้ง", en: "Scan not found for this code. Please scan the QR code again" },
  409: { th: "แบบสอบถามนี้ถูกตอบไปแล้ว", en: "This questionnaire has already been answered" },
  default: { th: "ส่งคำตอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", en: "Could not submit your answers. Please try again" }
};
const sortByOrder = items => [...items].sort((first, second) => (first.order || 0) - (second.order || 0) || first.id - second.id);

export default {
  data() {
    return {
      isLoading: false,
      question: {},
      topics: [],
      pdpa: false,
      isSubmitting: false,
      submitErrorMessage: null
    };
  },
  computed: {
    useId() {
      return this.$route.params.userId;
    },
    companyId() {
      return this.$route.params.companyId;
    },
    brandId() {
      return this.$route.params.brandId;
    },
    productId() {
      return this.$route.params.productId;
    },
    code() {
      return this.$route.params.code;
    },
    qrCodeScanId() {
      return this.$route.params.qrCodeScanId
    },
    isSendQuestionDisabled() {
      return this.isSubmitting || this.topics.some(topic => this.isRequired(topic) && !this.isAnswered(topic)) || this.pdpa === false
    },
    isNarak() {
      return Number(this.companyId) === COMPANY_IDS.NARAK;
    },
    isKn() {
      return Number(this.companyId) === COMPANY_IDS.KN;
    }
  },
  mounted() {
    this.getQuestion();
  },
  methods: {
    isRequired(topic) {
      return topic.required !== false;
    },
    isAnswered(topic) {
      return Boolean(topic.answer) && topic.answer.length > 0;
    },
    clearAnswerWhenEdited(topic, value) {
      if (value !== topic.answer) topic.answer = null;
    },
    filterChoiceNames(topic) {
      const search = topic.answer === topic.search ? "" : topic.search.trim().toLowerCase();
      return topic.questionaireChoices
        .map(choice => choice.name)
        .filter(name => name.toLowerCase().includes(search));
    },
    async getQuestion() {
      this.isLoading = true;
      try {
        const { statusCode, data } = await this.$axios.get(
          `/question/use/${this.useId}`
        );

        if (statusCode === 200) {
          const question = data.questionaires[0];
          const topics = sortByOrder(question.questionaireTopics);
          topics.forEach((topic, index) => {
            topic.number = index + 1;
            topic.answer = topic.type == "MULTI" ? [] : null;
            topic.search = "";
            topic.questionaireChoices = sortByOrder(topic.questionaireChoices || []);
          });

          this.question = question;
          this.topics = topics;
        }

        this.getCompany();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getCompany() {
      const { statusCode, data } = await this.$axios.get(
        `/crm/code/${this.code}/company`
      );

      if (statusCode === 200) {
        document.documentElement.style.setProperty("--color-primary", data.color)
      }
    },
    toSubmitErrorMessage(statusCode) {
      const messages = SUBMIT_ERROR_MESSAGES[statusCode] || SUBMIT_ERROR_MESSAGES.default;
      return this.isNarak ? messages.en : messages.th;
    },
    async sendQuestion() {
      if (this.isSubmitting) return;

      const questionAnswers = this.topics
        .filter(topic => this.isAnswered(topic))
        .map(topic => ({
          topicId: topic.id,
          question: topic.name,
          answer: topic.answer.toString(),
          values: [].concat(topic.answer)
        }));

      const req = {
        companyId: this.companyId,
        brandId: this.brandId,
        productId: this.productId,
        qrCodeCode: this.code,
        title: this.question.title,
        detail: this.question.detail,
        questionAnswers: questionAnswers,
        qrCodeScanId: this.qrCodeScanId,
        questionaireId: this.question.id,
        pdpa: this.pdpa
      };

      this.isSubmitting = true;
      this.submitErrorMessage = null;
      try {
        const { statusCode } = await this.$axios.post(
          `/answer/`, req
        );
        if (statusCode !== ANSWER_SAVED_STATUS_CODE) {
          this.submitErrorMessage = this.toSubmitErrorMessage(statusCode);
          return;
        }
        this.$router.push(`/${this.code}/finish`)
      } catch (error) {
        console.error(error);
        this.submitErrorMessage = this.toSubmitErrorMessage();
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  background-color: var(--color-primary);
}

.question {
  p {
    font-size: 18px;
    font-weight: 600;
    color: #101010;
  }
}

.required-mark {
  color: #e53935;
}

.question .submit-error {
  font-size: 16px;
  color: #e53935;
}

.optional-mark {
  font-size: 14px;
  font-weight: normal;
  color: #8e8f93;
}

.topic {
  border-radius: 12px;
  background-color: #d3dce624;
}
</style>